import { InfiniteData } from '@tanstack/query-core';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';

import {
  WoltInboundLoad,
  WoltInboundLoads,
  ListMetaWoltGoodsReceipts,
} from 'common/api-types/warehouse';
import { UseInfiniteQueryConfig, UseQueryConfig } from 'common/query/types';
import { useApiQueryClient } from 'common/query/useApiQueryClient';
import { AxiosWarehouseError } from 'common/types';
import { isNotNilInvariantAsync } from 'common/utils/invariant';

import {
  fetchGoodsReceipts,
  fetchInboundLoad,
  fetchInboundLoads,
  fetchPurchaseOrder,
  fetchVendors,
} from './api';
import { FetchGoodsReceiptsParams } from './types';

export const GOODS_RECEIPTS_QUERY_KEY_ROOT = 'goodsReceipts';
const INBOUND_LOADS_QUERY_KEY_ROOT = 'inboundLoads';
const PURCHASE_ORDERS_QUERY_KEY_ROOT = 'purchaseOrders';

const getInboundLoadListQueryKey = (queryKey: string, warehouseId: string, searchQuery: string) => [
  queryKey,
  'list',
  { warehouseId, searchQuery },
];

const getInboundLoadDetailQueryKey = (
  queryKey: string,
  warehouseId: string,
  inboundLoadId?: string,
) => [queryKey, 'detail', { warehouseId, inboundLoadId }];

type GoodsReceiptsQueryKey = [string, { warehouseId: string; searchQuery: string | undefined }];

export const useGoodsReceiptsInfiniteQuery = (
  warehouseId: string,
  params?: Omit<FetchGoodsReceiptsParams, 'page' | 'pageSize'>,
  options?: UseInfiniteQueryConfig<
    ListMetaWoltGoodsReceipts,
    AxiosWarehouseError,
    InfiniteData<ListMetaWoltGoodsReceipts>,
    GoodsReceiptsQueryKey,
    number
  >,
) => {
  return useInfiniteQuery({
    ...options,
    queryKey: [GOODS_RECEIPTS_QUERY_KEY_ROOT, { warehouseId, searchQuery: params?.q }],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      return fetchGoodsReceipts(warehouseId, {
        page: pageParam,
        pageSize: 20,
        from: params?.from,
        to: params?.to,
        q: params?.q,
        embed: params?.embed,
      });
    },
    getNextPageParam: (lastPageData) => {
      const isLastPage = lastPageData.meta.currentPage + 1 === lastPageData.meta.totalPages;

      if (!isLastPage) {
        return lastPageData.meta.currentPage + 1;
      }

      return undefined;
    },
  });
};

export const useInboundLoadsQuery = (
  warehouseId: string,
  searchQuery: string,
  options?: UseQueryConfig<WoltInboundLoads, AxiosWarehouseError>,
) => {
  return useQuery({
    ...options,
    queryKey: getInboundLoadListQueryKey(INBOUND_LOADS_QUERY_KEY_ROOT, warehouseId, searchQuery),
    queryFn: () => fetchInboundLoads(warehouseId, searchQuery),
  });
};

export const useInboundLoadQuery = (
  warehouseId: string,
  inboundLoadId?: string,
  options?: UseQueryConfig<WoltInboundLoad, AxiosWarehouseError>,
) => {
  const queryClient = useApiQueryClient();

  return useQuery({
    ...options,
    queryKey: getInboundLoadDetailQueryKey(
      INBOUND_LOADS_QUERY_KEY_ROOT,
      warehouseId,
      inboundLoadId,
    ),
    queryFn: () =>
      isNotNilInvariantAsync(
        inboundLoadId,
        'Query function should not be called with missing inboundLoadId, verify "enabled" condition',
        (inboundLoadId) => fetchInboundLoad(warehouseId, inboundLoadId),
      ),
    initialData: () => {
      const queriesData = queryClient.getQueriesData<WoltInboundLoad[]>({
        queryKey: [INBOUND_LOADS_QUERY_KEY_ROOT, 'list'],
      });

      const latestQuery = queriesData[queriesData.length - 1]?.[1];
      return latestQuery?.find((inboundLoad) => inboundLoad.id === inboundLoadId);
    },
  });
};

export const usePurchaseOrderDetailsQuery = (inboundLoadId: string) => {
  return useQuery({
    queryKey: [PURCHASE_ORDERS_QUERY_KEY_ROOT, { inboundLoadId }],
    queryFn: () => fetchPurchaseOrder(inboundLoadId),
    enabled: Boolean(inboundLoadId),
  });
};

export const useVendorsQuery = (filters: { countryCodes?: string[] }, enabled: boolean) => {
  return useQuery({
    queryKey: ['vendors', filters.countryCodes],
    queryFn: () => fetchVendors(filters),
    select: (data) =>
      data.items.map((vendor) => ({
        label: vendor.name,
        value: vendor.id,
      })),
    enabled,
  });
};
